"use client"

// =====================================================
// BOOTH — OAuth signup conversion (Meta + TikTok)
// =====================================================
// Password signups fire CompleteRegistration inline at the call site.
// Google signups can't: the account is created in app/auth/callback/route.ts,
// on the server, where there's no pixel. That route tags the post-signup
// redirect with ?booth_signup=<role> and this component converts the tag
// into the event, then strips it from the URL so a refresh or a shared link
// can't double-count.
//
// Deliberately ONE component firing both networks rather than one per
// pixel: the ?booth_signup flag is single-use, so two components racing to
// read it would mean whichever ran first stripped the URL and the other
// never saw the signup at all.
// =====================================================

import { Suspense, useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { trackMeta } from "@/lib/meta-pixel"
import { trackTikTok } from "@/lib/tiktok-pixel"
import { claimAttribution } from "@/lib/attribution-client"
import { newSignupEventId, reportSignupToMeta } from "@/lib/meta-capi-client"

function SignupConversionInner() {
  const searchParams = useSearchParams()
  const fired = useRef(false)

  useEffect(() => {
    const role = searchParams?.get("booth_signup")
    if (!role || fired.current) return
    fired.current = true

    const metaEventId = newSignupEventId()
    trackMeta("CompleteRegistration", { content_category: role, status: true }, metaEventId)
    trackTikTok("CompleteRegistration", { content_type: "product", content_id: role })

    // The OAuth account is already signed in by the time this runs — the
    // callback route established the session before redirecting here.
    claimAttribution()
    reportSignupToMeta(metaEventId)

    // replaceState rather than router.replace — no re-render, no refetch, and
    // the entry stays out of the back-button history.
    const url = new URL(window.location.href)
    url.searchParams.delete("booth_signup")
    window.history.replaceState({}, "", url.toString())
  }, [searchParams])

  return null
}

export function SignupConversion() {
  return (
    <Suspense fallback={null}>
      <SignupConversionInner />
    </Suspense>
  )
}
